/* eslint-disable no-use-before-define */
import { compress, decompress } from 'compress-json'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/react'
import { apps } from '#/constants'
import * as Helpers from '#/helpers'

export const attachmentToURL = (contents, filename) =>
  new Promise((resolve, reject) => {
    const fileId = `${nanoid(32)}/${filename}`
      .replace(/%20/g, ' ')
      .replace(/ /g, '-')

    let binary = ''
    const bytes = new Uint8Array(contents)
    const len = bytes.byteLength
    for (let i = 0; i < len; i += 1) {
      binary += String.fromCharCode(bytes[i])
    }

    fetch('https://api.appsplus.co/file', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileId,
        base64: window.btoa(binary),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data) {
          resolve(`https://api.appsplus.co/file/${fileId}`)
        } else {
          reject(new Error('No data returned'))
        }
      })
  })

export const fetchAttachment = (src, attachmentId, isJira) =>
  new Promise((resolve, reject) => {
    // direct Confluence download URL
    if (src?.includes('/download/attachments/')) {
      const fileUrl = new URL(src.replace('/wiki', '')).pathname
      const filename = fileUrl.split('/').pop()
      return AP.request({
        url: fileUrl,
        binaryAttachment: true,
        headers: {
          'X-Atlassian-Token': 'nocheck',
        },
      })
        .then(async ({ body }) => {
          return resolve({
            contents: body,
            filename,
          })
        })
        .catch((error) => reject(error))
    }

    // attachment ID or API
    AP.request(
      isJira
        ? `/rest/api/3/attachment/${attachmentId || src.split('/').pop()}`
        : `/api/v2/attachments/${attachmentId || src.split('/').pop()}`
    )
      .then((res) => JSON.parse(res.body))
      .then((attachment) => {
        AP.request({
          url: isJira ? attachment.content : attachment._links.download,
          binaryAttachment: !attachmentId,
          headers: {
            'X-Atlassian-Token': 'nocheck',
          },
        })
          .then(async ({ body }) => {
            let contents = body
            let filename = isJira ? attachment.filename : attachment.title
            // JSON files are uploaded via API
            if (filename?.includes('.json')) {
              try {
                // some are JSON, some are raw base64
                const data = JSON.parse(body)
                contents = data?.contents
                  ? await fetch(data.contents).then((res) => res.arrayBuffer())
                  : body
                filename = data?.filename
              } catch (e) {}
            }
            resolve({
              contents,
              filename,
            })
          })
          .catch(() => reject())
      })
      .catch(() => reject())
  })

export const fetchConfluenceAttachment = (uuid, attachmentId) => {
  // checks local attachment first in case of things like Comala Publishing copying
  return new Promise((resolve, reject) => {
    AP.context.getContext((context) => {
      const contentId = context.confluence.content.id
      const contentType = context.confluence.content.type
      AP.request({
        url: `/api/v2/${
          contentType === 'page' ? 'pages' : 'blogposts'
        }/${contentId}/attachments?filename=appsplus-tabs-data-${uuid}.json&limit=1`,
      })
        .then((res) => JSON.parse(res.body))
        .then(({ results }) => {
          if (results.length === 1) {
            resolve(results[0]._links.download)
          } else {
            AP.request(`/api/v2/attachments/${attachmentId}`)
              .then((res) => JSON.parse(res.body))
              .then((attachment) => {
                resolve(attachment._links.download)
              })
              .catch(reject)
          }
        })
    })
  })
}

// CONFLUENCE \\

export const fetchConfluenceMacro = (macro, setMacro, setTabs) => {
  AP.confluence.getMacroData((res) => {
    if (res && res.data) {
      try {
        // existing macro
        const data = JSON.parse(res.data)
        setMacro({ ...data, loaded: true })
      } catch (e) {
        // autoconvert link
        const { appExtract, linkExtract } = Helpers.extractLink(res.data)
        setMacro({ ...macro, loaded: true, isNew: false })
        setTabs({
          value: [
            {
              id: nanoid(10),
              config: {
                title: apps[appExtract].title.replace(/\s&.*$/, ''),
                app: appExtract.toLowerCase(),
                icon: appExtract.toLowerCase(),
                showIcon: true,
              },
              embed: {
                ...apps[appExtract].embed,
                url: linkExtract,
              },
            },
          ],
        })
      }
    } else {
      // new macro
      setMacro({ ...macro, loaded: true })
    }
  })
}

export const fetchConfluenceTabs = (
  macro,
  _tabs,
  _setTabs,
  setTabs,
  setLicensedApps,
  isEditor,
  isDialog
) => {
  if (macro?.attachmentId) {
    // existing macro
    Helpers.fetchConfluenceAttachment(macro.uuid, macro.attachmentId)
      .then((url) =>
        AP.request({
          url,
          headers: {
            'X-Atlassian-Token': 'nocheck',
          },
        })
          .then((res) => decompress(JSON.parse(res.body)))
          .then((data) => {
            if (isEditor) {
              _setTabs({ value: data })
            } else {
              if (!isDialog) {
                // non-dialog macro
                Helpers.licensedApps(
                  data.map((x) => x.config.app?.toUpperCase()),
                  macro?.demo
                ).then((x) => setLicensedApps(x))
              }
              setTabs(data)
            }
          })
          .catch((err) => console.error('Error downloading attachment:', err))
      )
      .catch((err) => console.error('Error fetching attachment:', err))
  } else if (_tabs.length > 0) {
    // autoconvert link
    Helpers.licensedApps(_tabs.map((x) => x.config.app?.toUpperCase())).then(
      (x) => setLicensedApps(x)
    )
  } else {
    // new macro
    _setTabs({ type: 'add' })
  }
}

export const fetchCustomThemes = (installedApp) =>
  AP.request(
    `/rest/atlassian-connect/1/addons/${installedApp}/properties/appsplusTabsThemes`
  )
    .then((res) => JSON.parse(res.body))
    .then((data) => data.value.themes)
    .catch(() => [])

export const contentStatus = () =>
  new Promise((resolve) => {
    AP.context.getContext((context) => {
      const contentId = context.confluence.content.id
      const contentType = context.confluence.content.type
      AP.request({
        url: `/api/v2/${
          contentType === 'page' ? 'pages' : 'blogposts'
        }/${contentId}`,
      })
        .then((res) => JSON.parse(res.body))
        .then(({ status }) =>
          resolve({
            id: contentId,
            type: contentType,
            status,
          })
        )
    })
  })

export const saveConfluence = async (appTitle, macro, tabs) => {
  const filteredTabs = macro?.demo ? tabs : tabs.filter((x) => x.config.app) // remove empty tabs unless is demo

  // create file blob
  try {
    const blob = new Blob([JSON.stringify(compress(filteredTabs))], {
      type: 'application/json',
    })

    const formData = new FormData()
    formData.append('file', blob, `appsplus-tabs-data-${macro.uuid}.json`)

    // save attachment
    const target = await Helpers.contentStatus()
    AP.request({
      url: `/rest/api/content/${target.id}/child/attachment?status=${target.status}`,
      type: 'PUT',
      headers: {
        'X-Atlassian-Token': 'nocheck',
      },
      contentType: 'multipart/form-data',
      data: {
        file: formData.get('file'),
        comment: `DO NOT DELETE [Data for ${appTitle}${
          !appTitle?.includes('+') ? '+' : ''
        }]`,
      },
    })
      .then((res) => JSON.parse(res.body))
      .then((data) => {
        AP.confluence.saveMacro({
          data: JSON.stringify({
            ...macro,
            attachmentId: data.results[0].id,
            loaded: false,
            isNew: false,
          }),
        })
        AP.dialog.close()
      })
      .catch((err) => {
        console.error('Error saving attachment:', err)
        throw err
      })
  } catch (e) {
    Sentry.addBreadcrumb({
      message: 'saveConfluence',
      data: {
        macro,
        tabs: JSON.stringify(tabs),
      },
    })
    Sentry.captureException(e)
  }
}

// JIRA \\

export const checkPermission = (setHasPermission) =>
  AP.request(`/rest/api/3/mypermissions?permissions=EDIT_ISSUES`)
    .then((res) => JSON.parse(res.body))
    .then((data) => {
      if (data) setHasPermission(data.permissions.EDIT_ISSUES.havePermission)
    })
    .catch(() => setHasPermission(false))

export const fetchJiraActive = (app) => {
  const updateActive = (url) =>
    AP.request({
      url,
      type: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify({ app }),
    }).catch((err) => console.error('Error setting Jira active:', err))

  AP.context.getContext((context) => {
    if (!context.jira?.issue) return // is dashboard
    const url = `/rest/api/3/issue/${context.jira.issue.id}/properties/appsplusEmbedActive`
    AP.request(url)
      .then((res) => JSON.parse(res.body))
      .then(({ value }) => {
        if (value?.app !== app) updateActive(url)
      })
      .catch(() => updateActive(url))
  })
}

export const fetchJiraMacro = (app, macro, setMacro) => {
  Helpers.propertyURL('macro').then((url) => {
    AP.request(url)
      .then((res) => JSON.parse(res.body))
      .then((data) => {
        if (data && data.value) setMacro({ ...data.value, loaded: true })
      })
      .catch(async () => {
        // new macro, check and apply custom theme default
        let theme = []
        try {
          const defaultTheme = await Helpers.fetchJiraDefaultTheme()
          if (defaultTheme) {
            const themes = Helpers.themes(true)
            const custom = await Helpers.fetchCustomThemes(
              apps[app.toUpperCase()].keys.jira
            )
            theme = [...themes, ...custom].find((x) => x.theme === defaultTheme)
          }
        } catch (error) {
          console.error('Error fetching theme:', error)
        }
        setMacro({ ...macro, ...theme, loaded: true })
      })
  })
}

export const fetchJiraTabs = (_setTabs, setTabs, isEditor) => {
  Helpers.propertyURL('tabs').then((url) => {
    Helpers.getProperty(url).then((data) => {
      if (data && data.length > 0) {
        if (isEditor) {
          // editor and macro are the same
          _setTabs({ value: data })
        } else {
          setTabs(data)
        }
      } else {
        Helpers.fetchJiraTemplateTabs(_setTabs)
      }
    })
  })
}

export const saveJira = (macro, tabs, toggleChanged, setSavingJira) => {
  setSavingJira(true)
  try {
    // save macro
    Helpers.propertyURL('macro').then((url) => {
      AP.request({
        url,
        type: 'PUT',
        contentType: 'application/json',
        data: JSON.stringify({ ...macro, loaded: false, isNew: false }),
      }).catch((err) => console.error('Error saving macro:', err))
    })
    // save tabs
    Helpers.propertyURL('tabs').then((url) => {
      const filteredTabs = macro?.demo ? tabs : tabs.filter((x) => x.config.app) // remove empty tabs unless is demo

      Helpers.setProperty(url, filteredTabs).then(() =>
        setTimeout(() => {
          toggleChanged(false)
          setSavingJira(false)
        }, 2000)
      )
    })
  } catch (e) {
    Sentry.addBreadcrumb({
      message: 'saveJira',
      data: {
        macro,
        tabs: JSON.stringify(tabs),
      },
    })
    Sentry.captureException(e)
  }
}

// JIRA TEMPLATES

export const fetchJiraTemplateTabs = async (_setTabs) => {
  AP.context.getContext((context) => {
    if (!context.jira?.project?.id) return _setTabs({ type: 'add' }) // dashboard item probably
    AP.request(
      `/rest/api/3/project/${context.jira.project.id}/properties/appsplusEmbedTemplate`
    )
      .then((res) => JSON.parse(res.body))
      .then(({ value }) => {
        const hasTemplate = value && value.template
        if (!hasTemplate) return _setTabs({ type: 'add' })

        if (value.template.json) {
          // add JSON data
          try {
            const tabs = JSON.parse(value.template.data)
            _setTabs({ value: tabs })
          } catch (e) {
            _setTabs({ type: 'add' })
          }
        } else {
          // add separate tabs
          const links = value.template.tabs
          if (links.length === 0) return _setTabs({ type: 'add' })
          const tabs = links.map(({ id, title, url }) => {
            const { appExtract, linkExtract } = Helpers.extractLink(url)
            const appKey = appExtract || 'WEBSITE'
            return {
              id,
              config: {
                app: appKey?.toLowerCase(),
                title: title || apps[appKey].title,
                icon: appKey?.toLowerCase(),
                showIcon: true,
              },
              embed: {
                ...apps[appKey].embed,
                url: apps[appKey].embedURL({ url: linkExtract }),
              },
            }
          })
          _setTabs({ value: tabs })
        }
      })
      .catch(() => _setTabs({ type: 'add' }))
  })
}

export const fetchJiraTemplates = async (primaryApp, projectList) => {
  let app = primaryApp // default app
  let theme = 'Atlas White' // default theme
  const projects = [] // auto display
  const hide = {} // quick-add
  const templates = []
  await Promise.all(
    projectList.map(({ value: key }) =>
      AP.request(`/rest/api/3/project/${key}/properties/appsplusEmbedTemplate`)
        .then((res) => JSON.parse(res.body))
        .then((data) => {
          if (data.value.app) {
            app = data.value.app
            projects.push(key)
          }
          if (data.value.theme) {
            theme = data.value.theme
          }
          if (data.value.hide) {
            data.value.hide.forEach((x) => {
              if (!hide[x]) hide[x] = []
              hide[x].push(key)
              hide[x] = [...new Set(hide[x])]
            })
          }
          if (data.value.template) templates.push(data.value.template)
        })
        .catch(() => null)
    )
  )
  const uniqueTemplates = templates.filter(
    (template, index, self) =>
      self.findIndex((t) => t.id === template.id) === index
  )
  return { app, theme, projects, hide, templates: uniqueTemplates }
}

export const fetchJiraDefaultTheme = () =>
  new Promise((resolve) => {
    AP.context.getContext((context) => {
      if (!context.jira?.project?.key) return resolve(null) // likely a dashboard, not an issue
      AP.request(
        `/rest/api/3/project/${context.jira.project.key}/properties/appsplusEmbedTemplate`
      )
        .then((res) => JSON.parse(res.body))
        .then((data) => {
          if (data && data.value.theme) resolve(data.value.theme)
        })
        .catch(() => resolve(null))
    })
  })

export const saveJiraTemplates = (projectList, config, setSaving) => {
  const { app, theme, projects, hide, templates } = config
  setSaving(true)
  projectList.forEach(({ value: key }) => {
    // handle quick-add hide
    const filteredHide = {}
    Object.entries(hide).forEach(([k, v]) => {
      v.forEach((x) => {
        if (!filteredHide[x]) filteredHide[x] = []
        filteredHide[x].push(k)
      })
    })

    // handle templates
    const foundTemplate = templates.find((x) =>
      x.projects.some((y) => y === key)
    )
    const filteredTemplate = foundTemplate
      ? {
          ...foundTemplate,
          tabs: foundTemplate.tabs.filter(
            (tab) => tab.url !== '' && tab.url.includes('.')
          ),
        }
      : null

    // save it
    AP.request({
      url: `/rest/api/3/project/${key}/properties/appsplusEmbedTemplate`,
      type: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify({
        app: projects.includes(key) ? app : null,
        theme,
        hide: filteredHide[key] || [],
        template: filteredTemplate,
      }),
    }).catch((err) => console.error('Error saving template:', err))
  })
  setTimeout(() => {
    setSaving(false)
  }, 2000)
}
